import { getUserColor } from '@tabeeb/shared/utils/color'

import {
  CreateTrackingRequest,
  TrackingAnnotationType,
  TrackingEventType,
  TrackingResponse,
} from '@tabeeb/contracts/main-api/swagger-main-api'

import Marker from '../components/Marker'
import Circle from '../components/Circle'
import Rectangle from '../components/Rectangle'

interface TrackingComponentProps {
  color: string
  [key: string]: unknown
}

export const convertTrackingToMapComponent = (
  tracking: TrackingResponse,
  props: TrackingComponentProps = { color: '' }
) => {
  const componentProps = { ...props, color: getUserColor(tracking.User) }

  switch (tracking.AnnotationType) {
    case TrackingAnnotationType.Point:
      return (
        <Marker
          key={tracking.Id}
          latitude={tracking.Coordinates![0].Latitude}
          longitude={tracking.Coordinates![0].Longitude}
          {...componentProps}
        />
      )
    case TrackingAnnotationType.Circle:
      return (
        <Circle key={tracking.Id} start={tracking.Coordinates![0]} end={tracking.Coordinates![1]} {...componentProps} />
      )
    case TrackingAnnotationType.Rectangle:
      return (
        <Rectangle
          key={tracking.Id}
          start={tracking.Coordinates![0]}
          end={tracking.Coordinates![1]}
          {...componentProps}
        />
      )
  }

  return null
}

interface MarkerToHotspotParams {
  marker: google.maps.Marker
  contentId: number
  userId: number
}

export const convertMarkerToHotspotTracking = ({
  marker,
  contentId,
  userId,
}: MarkerToHotspotParams): CreateTrackingRequest => {
  const position = marker.getPosition()

  return {
    EventType: TrackingEventType.Manual,
    AnnotationType: TrackingAnnotationType.Point,
    ContentId: contentId,
    UserId: userId,
    Coordinates: [
      {
        Latitude: position?.lat() ?? 0,
        Longitude: position?.lng() ?? 0,
      },
    ],
  }
}

interface CircleToCircleParams {
  circle: google.maps.Circle
  contentId: number
  userId: number
}

export const convertCircleToCircleTracking = ({
  circle,
  contentId,
  userId,
}: CircleToCircleParams): CreateTrackingRequest => {
  const radius = circle.getRadius()
  const center = circle.getCenter()

  if (!center) {
    throw new Error('Circle center is not defined')
  }

  const start = google.maps.geometry.spherical.computeOffset(center, radius, 135)
  const end = google.maps.geometry.spherical.computeOffset(center, radius, -45)

  return {
    EventType: TrackingEventType.Manual,
    AnnotationType: TrackingAnnotationType.Circle,
    ContentId: contentId,
    UserId: userId,
    Coordinates: [
      {
        Latitude: start.lat(),
        Longitude: start.lng(),
      },
      {
        Latitude: end.lat(),
        Longitude: end.lng(),
      },
    ],
  }
}

interface RectangleToRectangleParams {
  rectangle: google.maps.Rectangle
  contentId: number
  userId: number
}

export const convertRectangleToRectangleTracking = ({
  rectangle,
  contentId,
  userId,
}: RectangleToRectangleParams): CreateTrackingRequest => {
  const bounds = rectangle.getBounds()

  if (!bounds) {
    throw new Error('Rectangle bounds are not defined')
  }

  const northEast = bounds.getNorthEast()
  const southWest = bounds.getSouthWest()

  return {
    EventType: TrackingEventType.Manual,
    AnnotationType: TrackingAnnotationType.Rectangle,
    ContentId: contentId,
    UserId: userId,
    Coordinates: [
      {
        Latitude: northEast.lat(),
        Longitude: northEast.lng(),
      },
      {
        Latitude: southWest.lat(),
        Longitude: southWest.lng(),
      },
    ],
  }
}
